import React, {
    useContext, useEffect, useMemo, useState 
} from 'react';
import {
    Box, Typography, useTheme 
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import CardContainer from '../../components/cardContainer';
import Card from '../../components/card';
import {
    DiagnosticFormContext 
} from '../../../../contexts/form';
import {
    TypographyVariants, TypographyWeights 
} from '../../../../figmaTheme';

export const PlanSection = (): JSX.Element => {
    const formContext = useContext(DiagnosticFormContext);
    const [skipAnimation, setSkipAnimation] = useState(false);
    const useStyles = useMemo(() => makeStyles((theme) => ({
        scrollableContainer: {
            overflow: 'scroll',
            scrollbarWidth: 'none',
            minHeight: '100%',
            width: '100vw',
            maxWidth: '692px',
            transform: 'translate(-24px, 0)',
            padding: '0 24px'
        },
        content: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
        },
        staticCheckmark: {
            width: '16.2px',
            height: '16.2px',
            margin: 'auto 0'
        },
        checkmark: {
            width: '80px',
            height: '80px',
            margin: '0 auto',
            transform: 'translate(0)',
            zIndex: 1,
            animation: `$shrinkCheckmark ${skipAnimation ? 0 : 0.5}s forwards 0s, $moveCheckmark ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 1}s, $fadeOut ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 1.5}s`,
        },
        sections: {
            height: '80px',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            columnGap: '10px',
            marginTop: '50px',
            opacity: 0,
            animation: `$fadeIn ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 0.5}s`
        },
        section: {
            height: '80px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            rowGap: '8px'
        },
        background: {
            margin: 'auto 0',
            backgroundColor: theme.palette.neutral['100'],
            borderRadius: '50%',
            width: '16px',
            height: '16px',
        },
        planBackground: {
            margin: 'auto 0',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: theme.palette.neutral['100'],
            borderRadius: '50%',
            width: '16px',
            height: '16px',
            alignItems: 'center',
            justifyContent: 'center',
            animation: `$changeColor ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 1}s, $semigrow ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 1}s, $grow ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 2}s`,
        },
        icon: {
            fill: theme.palette.neutral['100'],
        },
        goalsSection: {
            opacity: 1,
            animation: `$fadeOut ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 1.5}s`,
        },
        habitsSection: {
            opacity: 1,
            animation: `$fadeOut ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 1.5}s`,
        },
        aboutYouSection: {
            opacity: 0
        },
        planSection: {
            opacity: 1,
            animation: `$moveSection ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 2}s`,
        },
        planIcon: {
            opacity: 0,
            fill: theme.palette.tertiary.main,
            width: '50%',
            height: '50%',
            animation: `$fadeIn ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 1}s`,
        },
        targetSection: {
            opacity: 1,
            animation: `$fadeOut ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 1.5}s`,
        },
        title: {
            fontWeight: TypographyWeights.BOLD,
            textAlign: 'center',
            marginBottom: '8px',
            marginTop: '50px',
            opacity: 0,
            animation: `$fadeIn ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 2}s`
        },
        subtitle: {
            textAlign: 'center',
            opacity: 0,
            animation: `$fadeIn ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 2}s`
        },
        '@keyframes fadeIn': {
            '0%': {
                opacity: '0' 
            },
            '100%': {
                opacity: '1' 
            },
        },
        '@keyframes fadeOut': {
            '0%': {
                opacity: '1' 
            },
            '100%': {
                opacity: '0' 
            },
        },
        '@keyframes changeColor': {
            '0%': {
                backgroundColor: theme.palette.neutral['100'] 
            },
            '100%': {
                backgroundColor: theme.palette.tertiary.main 
            },
        },
        '@keyframes semigrow': {
            '0%': {
                width: '16px',
                height: '16px' 
            },
            '100%': {
                width: '24px',
                height: '24px' 
            },
        },
        '@keyframes grow': {
            '0%': {
                width: '24px',
                height: '24px' 
            },
            '100%': {
                width: '80px',
                height: '80px' 
            },
        },
        '@keyframes moveSection': {
            '0%': {
                transform: 'translate(0)' 
            },
            '100%': {
                transform: 'translate(-26px, 0)' 
            },
        },
        '@keyframes shrinkCheckmark': {
            '0%': {
                transform: 'translate(0)',
                width: '80px',
                height: '80px' 
            },
            '100%': {
                transform: 'translate(0px, 98px)',
                width: '16.2px',
                height: '16.2px' 
            },
        },
        '@keyframes moveCheckmark': {
            '0%': {
                transform: 'translate(0px, 98px)' 
            },
            '100%': {
                transform: 'translate(-5px, 98px)' 
            },
        },
    })), [skipAnimation]);
    const classes = useStyles();
    const theme = useTheme();
    useEffect(() => {
        setTimeout(() => {
            setSkipAnimation(true);
        }, 2500);
    }, []);

    const onClick = () => {
        if (!skipAnimation) setSkipAnimation(true);
        else formContext.goNext();
    };

    const onBackClick = () => {
        formContext.goBack();
    };

    return (
        <CardContainer
            sectionKey={'plan'}
            goBack={onBackClick}
            goNext={onClick}
            backgroundColor={theme.palette.white}
            hideHeaderText
        >
            <Card>
                <Box className={classes.scrollableContainer}>
                    <Box className={classes.content}>
                        <svg
                            className={classes.checkmark}
                            width="82"
                            height="82"
                            viewBox="0.5 0.5 82 82"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <circle
                                cx="41"
                                cy="41"
                                r="40.5"
                                fill={theme.palette.neutral['400']}
                                stroke={theme.palette.neutral['400']}
                            />
                            <path
                                d="M20.1 47.2 l12.1 10.1 c0 0 1.7 1.7 2.9 -1.5 l25.4 -32.1"
                                stroke={theme.palette.white}
                                strokeWidth="7.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                        <Box className={classes.sections}>
                            <Box className={`${classes.section} ${classes.goalsSection}`}>
                                <svg
                                    className={classes.staticCheckmark}
                                    width="82"
                                    height="82"
                                    viewBox="0.5 0.5 82 82"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <circle
                                        cx="41"
                                        cy="41"
                                        r="40.5"
                                        fill={theme.palette.neutral['400']}
                                        stroke={theme.palette.neutral['400']}
                                    />
                                    <path
                                        d="M20.1 47.2 l12.1 10.1 c0 0 1.7 1.7 2.9 -1.5 l25.4 -32.1"
                                        stroke={theme.palette.white}
                                        strokeWidth="7.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </Box>
                            <Box className={`${classes.section} ${classes.habitsSection}`}>
                                <svg
                                    className={classes.staticCheckmark}
                                    width="82"
                                    height="82"
                                    viewBox="0.5 0.5 82 82"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <circle
                                        cx="41"
                                        cy="41"
                                        r="40.5"
                                        fill={theme.palette.neutral['400']}
                                        stroke={theme.palette.neutral['400']}
                                    />
                                    <path
                                        d="M20.1 47.2 l12.1 10.1 c0 0 1.7 1.7 2.9 -1.5 l25.4 -32.1"
                                        stroke={theme.palette.white}
                                        strokeWidth="7.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </Box>
                            <Box className={`${classes.section} ${classes.aboutYouSection}`}>
                                <Box className={classes.background} />
                            </Box>
                            <Box className={`${classes.section} ${classes.planSection}`}>
                                <Box className={classes.planBackground}>
                                    <svg
                                        className={`${classes.icon} ${classes.planIcon}`}
                                        width="40"
                                        height="40"
                                        viewBox="0 0 40 40"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <g clipPath="url(#clip0_2205_63863)">
                                            <path
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M10.1817 0.5C11.0101 0.500207 11.6815 1.17195 11.6813 2.00038L11.6809 3.77344H28.3176L28.318 1.99963C28.3182 1.1712 28.99 0.499793 29.8184 0.5C30.6468 0.500207 31.3183 1.17195 31.318 2.00037L31.3176 3.77344H33.5C36.8137 3.77344 39.5 6.45973 39.5 9.77344V13.5918V16.5918V33.5C39.5 36.8137 36.8137 39.5 33.5 39.5H6.5C3.18629 39.5 0.5 36.8137 0.5 33.5V16.5918V13.5918V9.77344C0.5 6.45973 3.18629 3.77344 6.5 3.77344H8.68088L8.68132 1.99963C8.68153 1.1712 9.35327 0.499793 10.1817 0.5ZM28.3168 6.77344L28.3164 8.54508C28.3162 9.37351 28.9876 10.0452 29.816 10.0455C30.6445 10.0457 31.3162 9.37426 31.3164 8.54583L31.3168 6.77344H33.5C35.1569 6.77344 36.5 8.11658 36.5 9.77344V13.5918H3.5V9.77344C3.5 8.11658 4.84315 6.77344 6.5 6.77344H8.68013L8.67969 8.54508C8.67948 9.37351 9.35089 10.0452 10.1793 10.0455C11.0077 10.0457 11.6795 9.37426 11.6797 8.54583L11.6801 6.77344H28.3168ZM36.5 16.5918V33.5C36.5 35.1569 35.1569 36.5 33.5 36.5H6.5C4.84315 36.5 3.5 35.1569 3.5 33.5V16.5918H36.5Z"
                                                fill={theme.palette.black}
                                            />
                                        </g>
                                        <defs>
                                            <clipPath id="clip0_2205_63863">
                                                <rect
                                                    width="40"
                                                    height="40"
                                                    fill={theme.palette.white}
                                                />
                                            </clipPath>
                                        </defs>
                                    </svg>
                                </Box>
                            </Box>
                            <Box className={`${classes.section} ${classes.targetSection}`}>
                                <Box className={classes.background} />
                            </Box>
                        </Box>
                        <Typography
                            variant={TypographyVariants.H2}
                            className={classes.title}
                        >
                            {`${formContext.answers.your_name ? `${formContext.answers.your_name}'s` : 'Your'} Sunnyside Plan`}
                        </Typography>
                        <Typography
                            variant={TypographyVariants.H5}
                            className={classes.subtitle}
                        >
                            {'Based on your responses, we think you\'re a great fit for Sunnyside. Now it\'s time to see your custom plan!'}
                        </Typography>
                    </Box>
                </Box>
            </Card>
        </CardContainer>
    );
};

export default PlanSection;
